import {useHaligator} from '@synako/haligator';
import {RootDesc as FFConnectRoot} from 'api-types/ffconnect-hal';
import {useCallback} from 'react';
import {useWebSocket} from './useWebSocket';
import {
  useReconnectInvalidation,
  useRegisterReconnectInvalidation,
} from './ReconnectInvalidationContext';

/**
 * Composant qui initialise la connexion WebSocket globale pour les notifications temps réel.
 *
 * À monter une seule fois au niveau racine de l'application (après HaligatorProvider
 * et ReconnectInvalidationProvider).
 * Chaque onglet du navigateur aura sa propre connexion WebSocket.
 *
 * Quand le serveur envoie une notification `cache-invalidation`, le cache halitrek
 * est automatiquement invalidé et les composants qui utilisent les resources
 * concernées se re-rendront avec les nouvelles données.
 *
 * À la reconnexion (ex: après coupure par Cloud Run toutes les ~10 min),
 * tous les callbacks enregistrés via useRegisterReconnectInvalidation sont appelés
 * pour rattraper les notifications manquées pendant la fenêtre de déconnexion.
 */
export function HaliWebSocket() {
  const {client} = useHaligator();
  const invokeAllInvalidations = useReconnectInvalidation();

  // Enregistre l'invalidation du canal collaboratif via le registre
  useRegisterReconnectInvalidation(
    useCallback(() => {
      client.logger.info(
        'WS: Reconnexion détectée, invalidation du cache conversations',
      );
      const api = client.go<FFConnectRoot>();
      const me = api.follow('get-me');
      const staleUris: string[] = [];

      // Client web + détail backoffice : participations-conversation
      // Cascade via invByRevLinks → conversations individuelles + messages
      const participationsUri = me
        .follow('participations-conversation')
        .getCache()?.uri;
      if (participationsUri) staleUris.push(participationsUri);

      // Liste backoffice : find-conversations (avec perimetre)
      const findConversationsUri = api
        .follow('find-conversations', {perimetre: true})
        .getCache()?.uri;
      if (findConversationsUri) staleUris.push(findConversationsUri);

      if (staleUris.length > 0) {
        client.handleResourceValidity(staleUris, []);
      }
    }, [client]),
  );

  // À la reconnexion, appelle tous les callbacks enregistrés (canal collaboratif, PDF processus, etc.)
  const handleReconnect = useCallback(() => {
    invokeAllInvalidations?.();
  }, [invokeAllInvalidations]);

  // Le hook gère toute la logique WebSocket (connexion, messages, reconnexion)
  // Sans sessionToken → authentification par cookie (web)
  useWebSocket(client, {onReconnect: handleReconnect});

  return null;
}
